import {
  KVAnnotation,
  KVMeasurement,
  KVPlaneIntersection,
  KVPolygonGeometry,
  KVPosition,
} from 'types'

export enum STEP_FILE_TYPES {
  MEASUREMENTS = 'measurements',
  PLANE_INTERSECTION = 'plane_intersection',
  ALL = 'all',
}

export function getAllStepData(
  measurements: KVMeasurement[] | undefined,
  planeIntersections: KVPlaneIntersection[] | undefined,
  polygonAnnotations: KVAnnotation[] | undefined,
  decimalPlaces: number = 3
): string {
  const hasMeasurements = measurements && measurements.length > 0
  const hasPlaneIntersections =
    planeIntersections && planeIntersections.length > 0

  if (!hasMeasurements && !hasPlaneIntersections) {
    return generateEmptyStepFile()
  }

  const stepHeader = generateStepHeader(STEP_FILE_TYPES.ALL)
  let entityId = 1

  const representationContextId = entityId++
  let stepData = `#${representationContextId} = GEOMETRIC_REPRESENTATION_CONTEXT(3);\n`

  const geometricItems: number[] = []

  if (hasMeasurements) {
    const measurementResult = generateStepDataInternal(
      measurements!,
      entityId,
      decimalPlaces
    )
    stepData += measurementResult.data
    geometricItems.push(...measurementResult.geometricItems)
    entityId = measurementResult.nextEntityId
  }

  if (hasPlaneIntersections) {
    const planeIntersectionResult = generatePlaneIntersectionStepDataInternal(
      planeIntersections!,
      entityId,
      decimalPlaces
    )
    stepData += planeIntersectionResult.data
    geometricItems.push(...planeIntersectionResult.geometricItems)
    entityId = planeIntersectionResult.nextEntityId
  }

  if (polygonAnnotations) {
    const polygonAnnotationResult = generatePolygonAnnotationStepDataInternal(
      polygonAnnotations!,
      entityId,
      decimalPlaces
    )
    stepData += polygonAnnotationResult.data
    geometricItems.push(...polygonAnnotationResult.geometricItems)
    entityId = polygonAnnotationResult.nextEntityId
  }

  if (geometricItems.length > 0) {
    const shapeRepresentationId = entityId++
    const itemsList = geometricItems.map((id) => `#${id}`).join(', ')
    stepData += `#${shapeRepresentationId} = GEOMETRIC_REPRESENTATION('', (${itemsList}), #${representationContextId});\n`
  }

  const stepFooter = generateStepFooter()
  return stepHeader + stepData + stepFooter
}

export function convertMeasurementsToStepFile(
  measurements: KVMeasurement[] | undefined,
  decimalPlaces: number = 3
): string {
  if (!measurements || measurements.length === 0) {
    return generateEmptyStepFile()
  }

  const stepHeader = generateStepHeader(STEP_FILE_TYPES.MEASUREMENTS)
  const stepData = generateStepData(measurements, decimalPlaces)
  const stepFooter = generateStepFooter()

  return stepHeader + stepData + stepFooter
}

export function convertPlaneIntersectionToStepFile(
  planeIntersection: KVPlaneIntersection | undefined,
  decimalPlaces: number = 3
): string {
  if (
    !planeIntersection ||
    planeIntersection.planeIntersectionSegments.length === 0
  ) {
    return generateEmptyStepFile()
  }

  const stepHeader = generateStepHeader(STEP_FILE_TYPES.PLANE_INTERSECTION)
  const stepData = generatePlaneIntersectionStepData(
    planeIntersection,
    decimalPlaces
  )
  const stepFooter = generateStepFooter()

  return stepHeader + stepData + stepFooter
}

function parsePosition(positionString: string): KVPosition | null {
  try {
    // Position strings are in format "X=-2.512 Y=213.841 Z=252.967"
    const xMatch = positionString.match(/X=([-\d.]+)/)
    const yMatch = positionString.match(/Y=([-\d.]+)/)
    const zMatch = positionString.match(/Z=([-\d.]+)/)

    if (xMatch && yMatch && zMatch) {
      return new KVPosition(
        parseFloat(xMatch[1]),
        parseFloat(yMatch[1]),
        parseFloat(zMatch[1])
      )
    }
  } catch (error) {
    console.warn('Failed to parse position string:', positionString, error)
  }
  return null
}

/**
 * Convert coordinates from Unreal Engine coordinate system to CAD coordinate system
 * Unreal: Left-handed, Z-up
 * CAD: Right-handed, Z-up
 */
function convertCoordinates(point: KVPosition): KVPosition {
  // Convert from left-handed to right-handed coordinate system
  // by negating the Y-axis
  return new KVPosition(point.x, point.z, point.y)
}

function generateStepHeader(stepFileType: STEP_FILE_TYPES): string {
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-')

  return `ISO-10303-21;
HEADER;
FILE_DESCRIPTION(('Data exported from Kestrel'), '2;1');
FILE_NAME('${stepFileType}_${timestamp}.step', '${new Date().toISOString()}', ('Kestrel'), ('Gecko Robotics'), 'Kestrel Export', 'Kestrel', '');
FILE_SCHEMA(('AUTOMOTIVE_DESIGN { 1 2 10303 214 0 1 1 1 }'));
ENDSEC;

DATA;
`
}

function generateStepData(
  measurements: KVMeasurement[],
  decimalPlaces: number = 3
): string {
  let stepData = ''
  let entityId = 1

  // Minimal STEP structure for maximum compatibility
  const representationContextId = entityId++
  stepData += `#${representationContextId} = GEOMETRIC_REPRESENTATION_CONTEXT(3);\n`

  const result = generateStepDataInternal(measurements, entityId, decimalPlaces)
  stepData += result.data

  if (result.geometricItems.length > 0) {
    const shapeRepresentationId = result.nextEntityId++
    const itemsList = result.geometricItems.map((id) => `#${id}`).join(', ')
    stepData += `#${shapeRepresentationId} = GEOMETRIC_REPRESENTATION('', (${itemsList}), #${representationContextId});\n`
  }

  return stepData
}

function generateStepDataInternal(
  measurements: KVMeasurement[],
  startEntityId: number,
  decimalPlaces: number
): { data: string; geometricItems: number[]; nextEntityId: number } {
  let stepData = ''
  let entityId = startEntityId
  const geometricItems: number[] = []

  measurements.forEach((measurement, measurementIndex) => {
    stepData += `/* Measurement: ${measurement.label || `Measurement ${measurementIndex + 1}`} */\n`

    if (measurement.segments && measurement.segments.length > 0) {
      measurement.segments.forEach((segment, segmentIndex) => {
        const startPoint = parsePosition(
          segment.measurementSegmentStartPosition
        )
        const endPoint = parsePosition(segment.measurementSegmentEndPosition)

        if (startPoint && endPoint) {
          // Apply coordinate system conversion
          const convertedStartPoint = convertCoordinates(startPoint)
          const convertedEndPoint = convertCoordinates(endPoint)

          const startPointId = entityId++
          const endPointId = entityId++
          const lineId = entityId++

          // Create label for this segment
          const measurementLabel =
            measurement.label || `Measurement ${measurementIndex + 1}`
          const segmentLabel = `${measurementLabel} - Segment ${segmentIndex + 1}`

          // Point definitions with labels
          stepData += `#${startPointId} = CARTESIAN_POINT('${segmentLabel} Start', (${convertedStartPoint.x.toFixed(decimalPlaces)}, ${convertedStartPoint.y.toFixed(decimalPlaces)}, ${convertedStartPoint.z.toFixed(decimalPlaces)}));\n`
          stepData += `#${endPointId} = CARTESIAN_POINT('${segmentLabel} End', (${convertedEndPoint.x.toFixed(decimalPlaces)}, ${convertedEndPoint.y.toFixed(decimalPlaces)}, ${convertedEndPoint.z.toFixed(decimalPlaces)}));\n`

          // Polyline with label
          stepData += `#${lineId} = POLYLINE('${segmentLabel}', (#${startPointId}, #${endPointId}));\n`

          geometricItems.push(lineId)
        }
      })
    }
  })

  return { data: stepData, geometricItems, nextEntityId: entityId }
}

function generatePlaneIntersectionStepData(
  planeIntersection: KVPlaneIntersection,
  decimalPlaces: number = 3
): string {
  let stepData = ''
  let entityId = 1

  // Minimal STEP structure: define a context
  const representationContextId = entityId++
  stepData += `#${representationContextId} = GEOMETRIC_REPRESENTATION_CONTEXT(3);\n`

  const result = generatePlaneIntersectionStepDataInternal(
    [planeIntersection],
    entityId,
    decimalPlaces
  )
  stepData += result.data

  if (result.geometricItems.length > 0) {
    const shapeRepresentationId = result.nextEntityId++
    const itemsList = result.geometricItems.map((id) => `#${id}`).join(', ')
    stepData += `#${shapeRepresentationId} = GEOMETRIC_REPRESENTATION('', (${itemsList}), #${representationContextId});\n`
  }
  return stepData
}

function generatePolygonAnnotationStepDataInternal(
  polygonAnnotations: KVAnnotation[],
  startEntityId: number,
  decimalPlaces: number = 3
): { data: string; geometricItems: number[]; nextEntityId: number } {
  let stepData = ''
  let entityId = startEntityId
  const geometricItems: number[] = []

  polygonAnnotations.forEach((polygonAnnotation) => {
    stepData += `/* Polygon Annotation: ${polygonAnnotation.label} */\n`
    if (polygonAnnotation.geometry?.kind === 'polygon') {
      const points = (polygonAnnotation.geometry as KVPolygonGeometry)?.points
      for (let pointIndex = 0; pointIndex < points.length; pointIndex++) {
        const point = points[pointIndex]
        const kvNextPoint =
          pointIndex === points.length - 1 ? points[0] : points[pointIndex + 1]

        const kvStartPoint = new KVPosition(point.x, point.y, point.z)
        const kvEndPoint = new KVPosition(
          kvNextPoint.x,
          kvNextPoint.y,
          kvNextPoint.z
        )

        const convertedPoint = convertCoordinates(kvStartPoint)
        const convertedEndPoint = convertCoordinates(kvEndPoint)
        const startPointId = entityId++
        const endPointId = entityId++
        const lineId = entityId++
        const endPointLabel =
          pointIndex === points.length - 1 ? 1 : pointIndex + 2
        stepData += `#${startPointId} = CARTESIAN_POINT('${polygonAnnotation.label} Point ${pointIndex + 1}', (${convertedPoint.x.toFixed(decimalPlaces)}, ${convertedPoint.y.toFixed(decimalPlaces)}, ${convertedPoint.z.toFixed(decimalPlaces)}));\n`
        stepData += `#${endPointId} = CARTESIAN_POINT('${polygonAnnotation.label} Point ${endPointLabel}', (${convertedEndPoint.x.toFixed(decimalPlaces)}, ${convertedEndPoint.y.toFixed(decimalPlaces)}, ${convertedEndPoint.z.toFixed(decimalPlaces)}));\n`
        stepData += `#${lineId} = POLYLINE('${polygonAnnotation.label} Point ${pointIndex + 1}', (#${startPointId}, #${endPointId}));\n`
        geometricItems.push(lineId)
      }
    }
  })

  return { data: stepData, geometricItems, nextEntityId: entityId }
}

function generatePlaneIntersectionStepDataInternal(
  planeIntersections: KVPlaneIntersection[],
  startEntityId: number,
  decimalPlaces: number
): { data: string; geometricItems: number[]; nextEntityId: number } {
  let stepData = ''
  let entityId = startEntityId
  const geometricItems: number[] = []

  planeIntersections.forEach((planeIntersection) => {
    stepData += `/* Plane Intersection: ${planeIntersection.planeIntersectionLabel} */\n`

    planeIntersection.planeIntersectionSegments.forEach(
      (point, segmentIndex) => {
        const kvStartPoint = new KVPosition(
          point.startPosition.x,
          point.startPosition.y,
          point.startPosition.z
        )
        const kvEndPoint = new KVPosition(
          point.endPosition.x,
          point.endPosition.y,
          point.endPosition.z
        )
        if (kvStartPoint && kvEndPoint) {
          const convertedStartPoint = convertCoordinates(kvStartPoint)
          const convertedEndPoint = convertCoordinates(kvEndPoint)

          const startPointId = entityId++
          const endPointId = entityId++
          const lineId = entityId++

          const planeIntersectionLabel =
            planeIntersection.planeIntersectionLabel || `PlaneIntersection`
          const segmentLabel = `${planeIntersectionLabel} - Segment ${segmentIndex + 1}`

          stepData += `#${startPointId} = CARTESIAN_POINT('${segmentLabel} Start', (${convertedStartPoint.x.toFixed(decimalPlaces)}, ${convertedStartPoint.y.toFixed(decimalPlaces)}, ${convertedStartPoint.z.toFixed(decimalPlaces)}));\n`
          stepData += `#${endPointId} = CARTESIAN_POINT('${segmentLabel} End', (${convertedEndPoint.x.toFixed(decimalPlaces)}, ${convertedEndPoint.y.toFixed(decimalPlaces)}, ${convertedEndPoint.z.toFixed(decimalPlaces)}));\n`
          stepData += `#${lineId} = POLYLINE('${segmentLabel}', (#${startPointId}, #${endPointId}));\n`

          geometricItems.push(lineId)
        }
      }
    )
  })

  return { data: stepData, geometricItems, nextEntityId: entityId }
}

function generateStepFooter(): string {
  return `ENDSEC;
END-ISO-10303-21;
`
}

function generateEmptyStepFile(): string {
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-')

  return `ISO-10303-21;
HEADER;
FILE_DESCRIPTION(('Empty measurement file - no measurements available'), '2;1');
FILE_NAME('empty_measurements_${timestamp}.step', '${new Date().toISOString()}', ('Kestrel'), ('Gecko Robotics'), 'Kestrel Export', 'Kestrel', '');
FILE_SCHEMA(('AUTOMOTIVE_DESIGN { 1 2 10303 214 0 1 1 1 }'));
ENDSEC;

DATA;
/* No measurements available */
ENDSEC;
END-ISO-10303-21;
`
}
